"use strict";

// Kept identical in the clients and receiver. Never serialize an Error,
// Axios config, response body, route params, or arbitrary context.
const APPS = ["customer-app", "hero-app", "customer-web", "website"];
const KINDS = ["handled", "render", "unhandled", "unhandled_promise", "api", "stalled"];
const TYPES = ["Error", "TypeError", "ReferenceError", "RangeError", "SyntaxError", "URIError", "EvalError", "FirebaseError", "AxiosError"];
const CODES = [
  "ERR_NETWORK", "ECONNABORTED", "ETIMEDOUT", "ERR_BAD_RESPONSE", "ERR_BAD_REQUEST",
  "permission-denied", "unavailable", "deadline-exceeded", "resource-exhausted",
  "unauthenticated", "internal", "unknown", "not-found", "failed-precondition",
  "firestore/permission-denied", "firestore/unavailable", "firestore/deadline-exceeded",
  "firestore/resource-exhausted", "firestore/unauthenticated", "firestore/internal",
  "firestore/unknown", "firestore/not-found", "firestore/failed-precondition", "firestore/aborted",
  "auth/network-request-failed", "auth/internal-error", "auth/too-many-requests",
  "storage/unauthorized", "storage/retry-limit-exceeded", "storage/unknown",
  "storage/object-not-found", "app-timeout",
];
const EXPECTED_CODES = [
  "ERR_CANCELED", "auth/user-cancelled", "auth/popup-closed-by-user",
  "Canceled", "Cancelled",
  "auth/cancelled-popup-request", "auth/wrong-password", "auth/invalid-credential",
  "auth/email-already-in-use", "auth/user-not-found", "auth/invalid-email",
  "auth/weak-password", "storage/canceled",
  "auth/account-exists-with-different-credential", "auth/requires-recent-login",
];
const ROUTE_WORDS = new Set([
  "api", "v1", "order", "orders", "invoice", "payment", "user", "users", "washer",
  "washers", "firebase", "account", "accounts", "engagement", "route", "interval",
  "start", "end", "create", "update", "delete", "get", "list", "me", "submit",
  "confirm", "cancel", "accept", "claim", "release", "upload", "download",
  "notifications", "support-chat", "pricing", "referral", "gift-cards", "stripe",
  "transfer", "delivery", "preferences", "banner-receipts", "client-errors",
  "washer-earnings", "duffloo", "send-email", "health", "legal", "auth",
  "login", "signup", "verify", "hero-verified", "app", "step", "training",
  "onboarding", "contract", "dashboard", "checkout", "cart", "support",
]);

const text = (value, pattern, max) =>
  typeof value === "string" && value.length <= max && pattern.test(value) ? value : "";
const tag = value => text(value, /^[a-zA-Z0-9_.-]+$/, 100);
const requestId = value => text(value, /^[a-zA-Z0-9+/=_-]{20,200}$/, 200);
const choose = (value, choices, fallback) => choices.includes(value) ? value : fallback;

function safePath(value) {
  if (typeof value !== "string") return "";
  return value.slice(0, 1000).split(/[?#]/)[0]
    .replace(/^[a-z]+:\/\/[^/]+/i, "")
    .split("/").filter(Boolean).slice(0, 8)
    .map(segment => ROUTE_WORDS.has(segment) ? segment : ":value")
    .reduce((path, segment) => path + "/" + segment, "") || "/";
}

// Only a source basename and numeric offsets survive; no error-message line,
// function arguments, URL hosts/query strings, local user directories or text.
function safeFrames(stack) {
  if (typeof stack !== "string") return [];
  return stack.slice(0, 12000).split("\n").slice(0, 35).flatMap(line => {
    if (!/^\s*at\s/.test(line) && !/^[^\s]*@/.test(line)) return [];
    // `jsbundle`: iOS loads main.jsbundle; without it every iOS frame was dropped.
    const match = line.match(/(?:^|[/\\\s(@])([A-Za-z0-9_.-]{1,100}\.(?:js|jsx|ts|tsx|bundle|jsbundle|hbc))(?::(\d{1,9}):(\d{1,9}))\)?\s*$/);
    return match ? [match[1] + ":" + match[2] + ":" + match[3]] : [];
  }).slice(0, 8);
}

function cleanFrames(frames) {
  return Array.isArray(frames) ? frames.slice(0, 8).filter(frame =>
    typeof frame === "string" && /^[A-Za-z0-9_.-]{1,100}\.(?:js|jsx|ts|tsx|bundle|jsbundle|hbc):\d{1,9}:\d{1,9}$/.test(frame)
  ) : [];
}

function fingerprint(report) {
  const key = [report.clientApp, report.kind, report.operation, report.errorType,
    report.errorCode, report.endpoint, report.status, ...report.frames.slice(0, 3)].join("|");
  let hash = 2166136261;
  for (let i = 0; i < key.length; i += 1) hash = Math.imul(hash ^ key.charCodeAt(i), 16777619);
  return "client-" + (hash >>> 0).toString(16).padStart(8, "0");
}

function normalizeReport(input) {
  if (!input || !APPS.includes(input.clientApp) || !KINDS.includes(input.kind)) return null;
  const status = Number.isInteger(input.status) && input.status >= 0 && input.status <= 599 ? input.status : 0;
  const report = {
    schemaVersion: 1,
    clientApp: input.clientApp,
    kind: input.kind,
    operation: tag(input.operation) || "unknown",
    errorType: choose(input.errorType, TYPES, "Error"),
    errorCode: choose(input.errorCode, CODES, ""),
    platform: choose(input.platform, ["ios", "android", "web"], "web"),
    clientMode: choose(input.clientMode, ["live", "test", "development"], "live"),
    clientRelease: tag(input.clientRelease || input.release) || "unknown",
    clientBuild: tag(input.clientBuild || input.build) || "",
    endpoint: input.endpoint ? safePath(input.endpoint) : "",
    method: choose(input.method, ["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"], ""),
    status,
    backendRequestId: requestId(input.backendRequestId),
    durationMs: Number.isFinite(input.durationMs) ? Math.max(0, Math.min(3600000, Math.round(input.durationMs))) : 0,
    frames: cleanFrames(input.frames),
  };
  report.severity = report.kind === "stalled" ||
    (report.kind === "api" && (status === 0 || status === 408 || status === 429)) ? "warn" : "error";
  report.fingerprint = fingerprint(report);
  return report;
}

function headerRequestId(headers) {
  try {
    return requestId(headers?.get?.("x-request-id") ||
      headers?.["x-request-id"] || headers?.["X-Request-Id"]);
  } catch (_) { return ""; }
}

function buildReport(error, context) {
  const response = error?.response;
  const config = error?.config;
  return normalizeReport({
    ...context,
    errorType: error?.name,
    errorCode: error?.code,
    frames: safeFrames(error?.stack),
    status: response?.status ?? context.status,
    endpoint: config?.url || context.endpoint,
    method: typeof config?.method === "string" ? config.method.toUpperCase() : context.method,
    backendRequestId: headerRequestId(response?.headers) || context.backendRequestId,
  });
}

function isExpected(error) {
  return error?.name === "AbortError" || EXPECTED_CODES.includes(error?.code) ||
    error?.__CANCEL__ === true ||
    // A card the bank declined (Stripe PaymentSheet result) is the customer's
    // outcome, not an app failure; other PaymentSheet failures still report.
    error?.type === "card_error" || (typeof error?.declineCode === "string" && error.declineCode !== "");
}

function shouldReportApi(error) {
  if (isExpected(error)) return false;
  const status = error?.response?.status;
  return status == null || status >= 500 || status === 408 || status === 429;
}

// Per session, bounded in time and memory. The same Error reaching an interceptor,
// a catch and a boundary is reported once, without modifying the original Error.
function createReporter(send, defaults, now = Date.now) {
  const seen = new WeakSet();
  const recent = new Map();
  let windowStart = now();
  let count = 0;
  return (error, operation = "unknown", context = {}) => {
    try {
      // The 4xx filter is for AUTOMATIC reports (interceptor, global handlers).
      // A developer's explicit recordHandledError is kept: a 400/401/422 on the
      // customer delivery email was exactly the failure that went unseen before.
      const kind = context?.kind || defaults?.kind;
      if (isExpected(error) ||
          (kind !== "handled" && error?.isAxiosError && !shouldReportApi(error))) return false;
      const objectError = error && (typeof error === "object" || typeof error === "function");
      if (objectError && seen.has(error)) return false;
      const report = buildReport(error, {...defaults, ...context, operation});
      if (!report) return false;
      if (objectError) seen.add(error);
      const time = now();
      if (time - windowStart >= 60000 || time < windowStart) {
        count = 0; windowStart = time; recent.clear();
      }
      if (count >= 10 || recent.has(report.fingerprint)) return false;
      recent.set(report.fingerprint, time);
      count += 1;
      Promise.resolve(send(report)).catch(() => {});
      return true;
    } catch (_) { return false; }
  };
}

module.exports = {
  APPS, KINDS, normalizeReport, buildReport, safePath, safeFrames, headerRequestId,
  createReporter, isExpected, shouldReportApi, tag,
};
